import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Lock, Loader2, Sparkles, CloudSun, Crown, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * iter122 — Festival packing-list card (Pro perk with free preview).
 *
 * Auto-opens when the user lands with `?packing=1` (the link inside the
 * 14-day reminder email). Otherwise rendered collapsed by default so the
 * page stays focused on the nearby-campsites grid.
 *
 * Free users see the top 5 essentials + a locked-count callout pushing
 * them into the 14-day Pro trial. Pro users see the full structured list.
 */
export default function FestivalPackingListCard({ slug, festivalName }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("packing") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!open || data) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/festivals/${slug}/packing-list`).then(({ data }) => {
      if (alive) setData(data);
    }).catch((e) => {
      if (alive) setError(e?.response?.data?.detail || "Couldn't build the packing list.");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, slug, data]);

  const isPro = !!user && (user.is_pro || user.role === "admin");

  return (
    <section
      className="mb-10 border-2 hairline-strong bg-white"
      data-testid="festival-packing-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-[#fafafa] transition-colors"
        data-testid="festival-packing-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#ff4500] text-white">
            <ListChecks className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div>
            <div className="eyebrow text-[#ff4500] mb-1">
              {isPro ? "Pro · packing list" : "Packing list · preview"}
            </div>
            <h3 className="font-display text-xl tracking-tight leading-tight">
              {festivalName ? `What to pack for ${festivalName}` : "Festival packing list"}
            </h3>
            <p className="text-xs mono text-[#666] uppercase tracking-wider mt-0.5 flex items-center gap-1.5">
              <CloudSun className="w-3 h-3" /> AI-built · uses the 7-day forecast for the site
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-[#666]" /> : <ChevronDown className="w-5 h-5 text-[#666]" />}
      </button>

      {open && (
        <div className="p-5 pt-0 border-t hairline">
          {loading && (
            <div className="flex items-center text-sm text-[#555] py-6">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Building your checklist…
            </div>
          )}

          {error && !loading && (
            <div className="text-sm text-[#dc2626] py-4">{error}</div>
          )}

          {data && !loading && data.is_pro_view && (
            <ProList data={data} />
          )}

          {data && !loading && !data.is_pro_view && (
            <FreePreview data={data} festivalName={festivalName} />
          )}
        </div>
      )}
    </section>
  );
}

function ProList({ data }) {
  return (
    <div data-testid="festival-packing-pro">
      <div className="flex items-center gap-2 text-xs mono text-[#666] uppercase tracking-wider mb-4">
        <Crown className="w-3.5 h-3.5 text-[#ff4500]" />
        {data.total_items} items · tailored to a {data.festival_audience} festival
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {data.sections.map((s) => (
          <div key={s.title} className="border hairline p-4">
            <div className="font-display text-base tracking-tight mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#ff4500]" /> {s.title}
            </div>
            <ul className="space-y-1.5 text-sm text-[#333]">
              {(s.items || []).map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#ff4500] mt-0.5">▢</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function FreePreview({ data, festivalName }) {
  return (
    <div data-testid="festival-packing-preview">
      <div className="border hairline p-4 mb-4 bg-[#fafafa]">
        <div className="font-display text-base tracking-tight mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#ff4500]" /> Free preview · top 5 essentials
        </div>
        <ul className="space-y-1.5 text-sm text-[#333]">
          {(data.preview_items || []).map((it, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#ff4500] mt-0.5">▢</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="border-2 border-[#ff4500] bg-[#fff3ec] p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="festival-packing-upsell"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2.5 bg-[#0e0e0e] text-[#ff4500]">
            <Lock className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight">
              {data.locked_items_count}+ more items waiting · tailored to {festivalName}
            </div>
            <div className="text-xs text-[#555] mt-1">
              Unlock the full checklist · weather-aware · audience-specific.
            </div>
          </div>
        </div>
        <Link
          to={data.upsell?.cta_url || "/pro"}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#ff4500] text-white hover:bg-[#e63e00] transition-colors text-xs mono uppercase tracking-wider flex-shrink-0"
          data-testid="festival-packing-cta"
        >
          <Crown className="w-3.5 h-3.5" />
          {data.upsell?.cta_label || "Try Pro free for 14 days"}
        </Link>
      </div>
    </div>
  );
}
